import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { LayoutDashboard, ClipboardList, LifeBuoy, LogOut, FolderOpen, Users, BarChart3, Bell, ArrowLeftRight, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import { useTheme } from "../context/ThemeContext";
import api from "../lib/api";
import NavHistoryButtons from "./NavHistoryButtons";
import PortalAnnouncementBanner from "./portal/PortalAnnouncementBanner";
import { PortalPermsProvider, usePortalPerms } from "../context/PortalPermsContext";

// Iter 427.p40 — Each nav tab declares the perm the portal user must
// hold to see it. The layout hides tabs the user can't access — not
// greying them out, DELETING them from the nav — per Dirk's POPIA
// requirement: "if maintenance shouldn't have accounts, the accounts
// tab should be REMOVED from the screen, not show zero rows."
// Owner preset holds every perm so they see the full nav.
const NAV = [
  { to: "/portal", label: "Overview", icon: LayoutDashboard, end: true, key: "overview", color: "#1FB6C1", perm: null },
  { to: "/portal/jobs", label: "My jobs", icon: ClipboardList, key: "jobs", color: "#F59E0B", perm: "portal.jobs.read" },
  { to: "/portal/tickets", label: "My tickets", icon: LifeBuoy, key: "tickets", color: "#F43F5E", perm: "portal.tickets.read" },
  { to: "/portal/documents", label: "Documents", icon: FolderOpen, key: "documents", color: "#A78BFA", perm: "portal.documents.read" },
  { to: "/portal/announcements", label: "Announcements", icon: Bell, key: "announcements", color: "#F59E0B", perm: "portal.announcements.read" },
  { to: "/portal/performance", label: "Performance", icon: BarChart3, key: "performance", color: "#22D3EE", perm: "portal.performance.read" },
  { to: "/portal/team", label: "Team", icon: Users, key: "team", color: "#10B981", perm: "portal.users.manage" },
];

export default function PortalLayout() {
  return (
    <PortalPermsProvider>
      <PortalLayoutInner />
    </PortalPermsProvider>
  );
}

function PortalLayoutInner() {
  const { user, logout } = useAuth();
  const { perms, loading: permsLoading } = usePortalPerms();
  // Iter 427.p40 — Filter the visible NAV to only tabs the user is
  // authorised for. Overview is always visible. While perms are still
  // loading (first render on a hard reload), we render the "safe"
  // subset (Overview only) so we never briefly leak a tab the user
  // shouldn't see.
  const visibleNav = useMemo(() => {
    if (permsLoading) return NAV.filter((n) => !n.perm);
    return NAV.filter((n) => !n.perm || perms?.[n.perm]);
  }, [perms, permsLoading]);
  const { branding } = useBranding();
  const { theme: portalTheme } = useTheme();
  const navigate = useNavigate();
  const brandName = branding?.app_name || "Rogue";
  // [Iter 290zp.13t] Theme-aware logo.
  const brandLogo =
    (portalTheme === "light"
      ? (branding?.logo_url_light || branding?.logo_url)
      : (branding?.logo_url_dark || branding?.logo_url))
    || "/icons/icon-192.png";

  // Iter 426 Phase 3 — Multi-customer session context.
  // Fetch this portal user's memberships once so we can:
  //   (a) render the "Signed in as … · {activeCustomer.name}" chip
  //   (b) surface a "Switch customer" shortcut only when there is
  //       actually more than one to switch to. Single-customer users
  //       see the same clean chrome as before.
  const [memberships, setMemberships] = useState([]);
  const [activeCustomer, setActiveCustomer] = useState(null);
  useEffect(() => {
    if (user?.role !== "client") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/portal/memberships");
        if (!alive) return;
        const items = data?.items || [];
        setMemberships(items);
        // Prefer JWT-driven scope; fall back to legacy users.customer_id.
        const activeId = user.customer_id_scope || user.customer_id;
        setActiveCustomer(
          items.find((m) => m.customer_id === activeId)?.customer || null,
        );
      } catch (_e) {
        // Silently ignore — chip just won't render.
      }
    })();
    return () => { alive = false; };
  }, [user?.id, user?.customer_id_scope, user?.customer_id, user?.role]);

  const doLogout = async (redirect = "/login") => {
    await logout();
    navigate(redirect);
  };

  const switchCustomer = () => doLogout("/login?switch=1");

  return (
    <div className="min-h-screen bg-[#0a0b0e] text-[#dde3ec] font-sans">
      <header className="sticky top-0 z-[60] bg-[#121419]/95 backdrop-blur-xl border-b border-white/10 hv-safe-top hv-safe-x">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <img
              src={brandLogo}
              alt={brandName}
              className="h-9 w-9 md:h-10 md:w-10 rounded-md object-contain shrink-0"
            />
            {/* Global navigation buttons — between logo and right-side utilities */}
            <NavHistoryButtons portalMode />
            <div className="hidden sm:block min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#647087]">{brandName}</div>
              <div className="font-display text-xl font-bold flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1fb6c1] animate-pulse" />
                Customer Portal
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Iter 426 Phase 3 — Active customer chip. Sits between the
                user name and the sign-out button so the operator ALWAYS
                sees which customer the current session is scoped to. */}
            {activeCustomer && (
              <div
                className="hidden md:flex items-center gap-2 pl-3 pr-3 py-1.5 rounded-full border border-[#1fb6c1]/40 bg-[#1fb6c1]/10"
                data-testid="portal-active-customer-chip"
                title={`Session is scoped to ${activeCustomer.name}. Sign out to switch.`}
              >
                {activeCustomer.logo_url ? (
                  <img
                    src={activeCustomer.logo_url}
                    alt=""
                    className="w-5 h-5 rounded-sm object-cover"
                  />
                ) : (
                  <Building2 className="w-3.5 h-3.5 text-[#1fb6c1]" />
                )}
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[#8ad0d6] leading-tight">Viewing</div>
                  <div className="text-xs font-semibold text-white truncate max-w-[180px] leading-tight">
                    {activeCustomer.name}
                  </div>
                </div>
              </div>
            )}
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium truncate max-w-[160px]">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087]">
                {activeCustomer ? "Portal user" : "Client"}
              </div>
            </div>
            {/* Iter 426 Phase 3 — Switch customer shortcut ONLY when
                the user actually has multiple memberships. Under the
                hood this logs out and returns to the login flow;
                because they land on /login with the picker email
                empty, they'll re-enter creds and hit the picker.
                A future iteration can pre-fill the email from a
                sessionStorage hint. */}
            {memberships.length > 1 && (
              <button
                onClick={switchCustomer}
                className="hv-btn hv-btn-ghost text-xs whitespace-nowrap"
                data-testid="portal-switch-customer"
                title="Sign out and pick a different customer."
              >
                <ArrowLeftRight className="w-4 h-4" />
                <span className="hidden lg:inline">Switch customer</span>
              </button>
            )}
            <button
              onClick={() => doLogout("/login")}
              className="hv-btn hv-btn-ghost"
              data-testid="portal-logout"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        {/* Iter 426 Phase 3 — On very small screens (< sm) the chip
            + user text are hidden. Surface the active customer in a
            dedicated strip so mobile portal users still see the
            hard-scope context at all times. */}
        {activeCustomer && (
          <div
            className="md:hidden flex items-center justify-center gap-1.5 py-1 border-t border-white/5 bg-[#0a0b0e]"
            data-testid="portal-active-customer-strip"
          >
            <Building2 className="w-3 h-3 text-[#1fb6c1]" />
            <div className="text-[10px] uppercase tracking-widest text-[#8ad0d6]">
              Viewing <span className="text-white font-semibold normal-case tracking-normal">{activeCustomer.name}</span>
            </div>
          </div>
        )}
        <nav className="max-w-6xl mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              end={n.end}
              data-testid={`portal-nav-${n.key}`}
              style={({ isActive }) => (isActive
                ? { color: n.color, borderColor: n.color, background: `${n.color}0d` }
                : undefined)}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-2.5 text-xs uppercase tracking-wider border-b-2 transition ${
                  isActive
                    ? ""
                    : "text-[#647087] border-transparent hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <PortalAnnouncementBanner />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] hv-fade-in">
        <Outlet />
      </main>
    </div>
  );
}
